'use client';

import HistoryIcon from '@mui/icons-material/History';
import { Box, Button, CircularProgress, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  fetchTomVendingSiteVisitHistoryList,
  type TomVendingSiteVisitHistoryListRow,
} from '@/hooks/useTomVendingSiteVisitHistory';
import { formatUsdFromCents } from '@/hooks/useTomVendingReports';
import { useNotification } from '@/contexts/NotificationContext';
import { formatVisitDateLabel } from '@/components/toms-vending/TomsVendingSiteVisitDetailContent';

const TomsVendingLocationSiteVisitHistoryPanel: React.FC<{
  locationTomVendingId: number;
}> = ({ locationTomVendingId }) => {
  const navigate = useNavigate();
  const { showError } = useNotification();
  const [rows, setRows] = useState<TomVendingSiteVisitHistoryListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchTomVendingSiteVisitHistoryList({
        locationTomVendingId,
        skip: 0,
        take: 100,
      });
      setRows(r.data);
      setTotal(r.total);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to load visit history');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [locationTomVendingId, showError]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const openDetail = (row: TomVendingSiteVisitHistoryListRow) => {
    navigate(
      `/app/toms-vending/site-visits/${row.id}?locationId=${locationTomVendingId}`,
    );
  };

  const rejectReportHref = `/app/toms-vending/reports/rejects?locationId=${locationTomVendingId}`;

  if (loading) {
    return (
      <Box>
        <Button
          component={RouterLink}
          to={rejectReportHref}
          variant="outlined"
          size="small"
          sx={{ textTransform: 'none', mb: 2 }}
        >
          View reject report for this location
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Button
          component={RouterLink}
          to={rejectReportHref}
          variant="outlined"
          size="small"
          sx={{ textTransform: 'none', mb: 2 }}
        >
          View reject report for this location
        </Button>
        <HistoryIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body1" color="text.secondary">
          No site visit history for this location yet.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          When someone records money collected or saves tallies from a site visit here, they will
          appear in this list.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Button
        component={RouterLink}
        to={rejectReportHref}
        variant="outlined"
        size="small"
        sx={{ textTransform: 'none', mb: 2 }}
      >
        View reject report for this location
      </Button>
      {total > rows.length && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Showing {rows.length} of {total} visits with saved data.
        </Typography>
      )}
      <List disablePadding>
        {rows.map((row) => (
          <ListItemButton key={row.id} onClick={() => openDetail(row)} sx={{ borderRadius: 1, mb: 0.5 }}>
            <ListItemText
              primary={formatVisitDateLabel(row.visitDate)}
              secondary={
                <>
                  Visit #{row.id}
                  {row.userDisplayName ? ` · ${row.userDisplayName}` : ''}
                  {(row.totalCashCollectedCents ?? 0) > 0
                    ? ` · Cash ${formatUsdFromCents(row.totalCashCollectedCents ?? 0)}`
                    : ''}
                </>
              }
            />
          </ListItemButton>
        ))}
      </List>
    </>
  );
};

export default TomsVendingLocationSiteVisitHistoryPanel;
